import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostsStore, usePlatformsStore, useUIStore, useAIStore } from '../stores';
import {
  Twitter,
  Instagram,
  Linkedin,
  Facebook,
  Youtube,
  Music,
  Pin,
  AtSign,
  Image,
  Video,
  Smile,
  Hash,
  Calendar,
  Clock,
  Sparkles,
  Loader2,
  X,
  Check,
  AlertCircle,
  Send,
  Wand2,
} from 'lucide-react';
import { format, addDays, addHours, setHours, setMinutes } from 'date-fns';

const platforms = [
  { id: 'twitter', name: 'Twitter', icon: Twitter, color: '#1DA1F2', maxLength: 280 },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F', maxLength: 2200 },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2', maxLength: 63206 },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2', maxLength: 3000 },
  { id: 'tiktok', name: 'TikTok', icon: Music, color: '#000000', maxLength: 2200 },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000', maxLength: 5000 },
  { id: 'pinterest', name: 'Pinterest', icon: Pin, color: '#E60023', maxLength: 500 },
  { id: 'threads', name: 'Threads', icon: AtSign, color: '#000000', maxLength: 500 },
];

const aiPrompts = [
  'Make it professional',
  'Add humor',
  'More engaging',
  'Shorter version',
  'Add call-to-action',
  'Make it viral',
];

export default function CreatePost() {
  const [content, setContent] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [media, setMedia] = useState([]);
  const [scheduleType, setScheduleType] = useState('now'); // 'now', 'schedule', 'optimal'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  // isAIGenerating comes from useAIStore (isAIGenerating)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  
  const { createPost, publishNow } = usePostsStore();
  const { platforms: connectedPlatforms } = usePlatformsStore();
  const { addToast } = useUIStore();
  const { generateCaption, isGenerating: isAIGenerating } = useAIStore();
  
  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };
  
  const handleMediaUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setMedia(prev => [...prev, {
          id: Math.random().toString(36).substr(2, 9),
          type: file.type.startsWith('video') ? 'video' : 'image',
          url: e.target.result,
          name: file.name,
        }]);
      };
      reader.readAsDataURL(file);
    });
  };
  
  const removeMedia = (id) => {
    setMedia(prev => prev.filter(m => m.id !== id));
  };
  
  const generateAICaption = async (prompt = '') => {
    const context = content ? `Existing draft: "${content}"` : '';
    const result = await generateCaption(
      prompt || 'Write a viral social media caption for my brand',
      context
    );
    if (result.success) {
      setContent(result.content);
      addToast({ type: 'success', title: 'Caption generated!', message: 'Claude AI created a caption for you.' });
    } else {
      addToast({ type: 'error', title: 'AI unavailable', message: result.error || 'Set ANTHROPIC_API_KEY in backend .env' });
    }
  };
  
  const handleSubmit = async () => {
    if (!content.trim()) {
      addToast({ type: 'error', title: 'Content required', message: 'Please write something to post.' });
      return;
    }
    
    if (selectedPlatforms.length === 0) {
      addToast({ type: 'error', title: 'Select platforms', message: 'Choose at least one platform.' });
      return;
    }
    
    setIsSubmitting(true);
    
    let scheduledAt = null;
    if (scheduleType === 'schedule' && scheduleDate && scheduleTime) {
      const [hours, minutes] = scheduleTime.split(':').map(Number);
      const date = new Date(scheduleDate);
      scheduledAt = setMinutes(setHours(date, hours), minutes).toISOString();
    } else if (scheduleType === 'optimal') {
      // Mock optimal time - tomorrow at 10 AM
      scheduledAt = setMinutes(setHours(addDays(new Date(), 1), 10), 0).toISOString();
    }
    
    const postData = {
      content,
      platforms: selectedPlatforms,
      media,
      scheduledAt,
    };
    
    const result = await createPost(postData);
    
    if (result.success) {
      if (scheduleType === 'now') {
        await publishNow(result.post.id);
        addToast({
          type: 'success',
          title: 'Post published!',
          message: `Your post is now live on ${selectedPlatforms.length} platform(s).`,
        });
      } else {
        addToast({
          type: 'success',
          title: 'Post scheduled!',
          message: `Your post will go live on ${format(new Date(scheduledAt), 'MMM d, h:mm a')}.`,
        });
      }
      navigate('/calendar');
    } else {
      addToast({ type: 'error', title: 'Error', message: 'Failed to create post.' });
    }
    
    setIsSubmitting(false);
  };
  
  const minCharLimit = Math.min(...selectedPlatforms.map(
    p => platforms.find(pl => pl.id === p)?.maxLength || 280
  ));
  
  const isOverLimit = content.length > minCharLimit && selectedPlatforms.length > 0;
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create Post</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Write once, publish everywhere
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main editor */}
        <div className="lg:col-span-2 space-y-4">
          {/* Platform selector */}
          <div className="card p-4">
            <label className="label block mb-3">Select platforms</label>
            <div className="flex flex-wrap gap-2">
              {platforms.map((platform) => {
                const isConnected = connectedPlatforms.find(p => p.id === platform.id)?.connected;
                const isSelected = selectedPlatforms.includes(platform.id);
                
                return (
                  <button
                    key={platform.id}
                    onClick={() => togglePlatform(platform.id)}
                    disabled={!isConnected}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${isSelected 
                        ? 'ring-2 ring-brand-500 bg-brand-50 dark:bg-brand-900/20' 
                        : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }
                      ${!isConnected && 'opacity-50 cursor-not-allowed'}
                    `}
                  >
                    <platform.icon 
                      className="w-4 h-4" 
                      style={{ color: isSelected ? platform.color : undefined }}
                    />
                    {platform.name}
                    {!isConnected && (
                      <span className="text-xs text-gray-400">(not connected)</span>
                    )}
                    {isSelected && (
                      <Check className="w-4 h-4 text-brand-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Content editor */}
          <div className="card">
            <div className="p-4">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What's on your mind? Write your post here..."
                className="w-full h-40 resize-none border-0 focus:ring-0 text-gray-900 dark:text-white bg-transparent placeholder:text-gray-400 text-lg"
              />
              
              {/* Media preview */}
              {media.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {media.map((item) => (
                    <div key={item.id} className="relative group">
                      {item.type === 'image' ? (
                        <img 
                          src={item.url} 
                          alt="" 
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      ) : (
                        <video 
                          src={item.url} 
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      )}
                      <button
                        onClick={() => removeMedia(item.id)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Toolbar */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleMediaUpload}
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
                  title="Add image"
                >
                  <Image className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
                  title="Add video"
                >
                  <Video className="w-5 h-5" />
                </button>
                <button 
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
                  title="Add emoji"
                >
                  <Smile className="w-5 h-5" />
                </button>
                <button 
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
                  title="Add hashtags"
                >
                  <Hash className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                {selectedPlatforms.length > 0 && (
                  <span className={`text-sm ${isOverLimit ? 'text-red-500' : 'text-gray-400'}`}>
                    {content.length}/{minCharLimit}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* AI Assistant */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-brand-500" />
              <span className="font-medium text-gray-900 dark:text-white">AI Assistant</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => generateAICaption()}
                disabled={isAIGenerating}
                className="btn-primary text-sm py-1.5 px-3"
              >
                {isAIGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                ) : (
                  <Wand2 className="w-4 h-4 mr-1.5" />
                )}
                Generate Caption
              </button>
              
              {aiPrompts.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => generateAICaption(prompt)}
                  disabled={isAIGenerating || !content}
                  className="btn-secondary text-sm py-1.5 px-3 disabled:opacity-50"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Sidebar - Scheduling */}
        <div className="space-y-4">
          <div className="card p-4">
            <label className="label block mb-3">When to post</label>
            
            <div className="space-y-2">
              <label className={`
                flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition-colors
                ${scheduleType === 'now' 
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }
              `}>
                <input
                  type="radio"
                  name="schedule"
                  checked={scheduleType === 'now'}
                  onChange={() => setScheduleType('now')}
                  className="sr-only"
                />
                <Send className="w-5 h-5 text-brand-500" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Post now</p>
                  <p className="text-xs text-gray-500">Publish immediately</p>
                </div>
              </label>
              
              <label className={`
                flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition-colors
                ${scheduleType === 'schedule' 
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }
              `}>
                <input
                  type="radio"
                  name="schedule"
                  checked={scheduleType === 'schedule'}
                  onChange={() => setScheduleType('schedule')}
                  className="sr-only"
                />
                <Calendar className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Schedule</p>
                  <p className="text-xs text-gray-500">Pick date & time</p>
                </div>
              </label>
              
              <label className={`
                flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition-colors
                ${scheduleType === 'optimal' 
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }
              `}>
                <input
                  type="radio"
                  name="schedule"
                  checked={scheduleType === 'optimal'}
                  onChange={() => setScheduleType('optimal')}
                  className="sr-only"
                />
                <Sparkles className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Optimal time</p>
                  <p className="text-xs text-gray-500">AI picks best time</p>
                </div>
              </label>
            </div>
            
            {scheduleType === 'schedule' && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="label block mb-1.5">Date</label>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label block mb-1.5">Time</label>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="input"
                  />
                </div>
              </div>
            )}
            
            {scheduleType === 'optimal' && (
              <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-700 dark:text-green-400">
                  <Clock className="w-4 h-4 inline mr-1.5" />
                  Best time: Tomorrow at 10:00 AM
                </p>
                <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                  Based on your audience's activity
                </p>
              </div>
            )}
          </div>
          
          {/* Post preview */}
          <div className="card p-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Preview</h3>
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
              {content ? (
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {content}
                </p>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  Your post preview will appear here...
                </p>
              )}
              {media.length > 0 && (
                <div className="mt-3 flex gap-1">
                  {media.slice(0, 4).map((item, i) => (
                    <div 
                      key={item.id} 
                      className="relative w-16 h-16 rounded overflow-hidden"
                    >
                      <img 
                        src={item.url} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                      {i === 3 && media.length > 4 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-medium">
                          +{media.length - 4}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !content.trim() || selectedPlatforms.length === 0 || isOverLimit}
            className="btn-primary w-full py-3 text-base disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                {scheduleType === 'now' ? 'Publishing...' : 'Scheduling...'}
              </>
            ) : (
              <>
                {scheduleType === 'now' ? (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Publish Now
                  </>
                ) : (
                  <>
                    <Calendar className="w-5 h-5 mr-2" />
                    Schedule Post
                  </>
                )}
              </>
            )}
          </button>
          
          {isOverLimit && (
            <p className="text-sm text-red-500 text-center flex items-center justify-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Content exceeds character limit
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
